import { useQuery } from "@tanstack/react-query";
import api from "../services/api";

interface MetricsData {
  users: {
    total: number;
    question_owners: number;
    responders: number;
  };
  surveys: {
    total: number;
    active: number;
    completed: number;
  };
  financials: {
    total_deposits: number;
    total_rewards_distributed: number;
    total_platform_fees: number;
  };
}

export default function AdminDashboardPage() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ["admin", "metrics"],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: MetricsData }>("/admin/metrics");
      return response.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading metrics...</div>
      </div>
    );
  }

  const stats = [
    {
      title: "Total Users",
      value: metrics?.users.total || 0,
      details: [
        { label: "Question Owners", value: metrics?.users.question_owners || 0 },
        { label: "Responders", value: metrics?.users.responders || 0 },
      ],
      icon: "👥",
      color: "bg-blue-500/20 text-blue-400",
    },
    {
      title: "Surveys",
      value: metrics?.surveys.total || 0,
      details: [
        { label: "Active", value: metrics?.surveys.active || 0 },
        { label: "Completed", value: metrics?.surveys.completed || 0 },
      ],
      icon: "📋",
      color: "bg-green-500/20 text-green-400",
    },
    {
      title: "Platform Fees",
      value: `$${metrics?.financials.total_platform_fees.toFixed(2) || "0.00"}`,
      details: [
        { label: "Total Deposits", value: `$${(metrics?.financials.total_deposits || 0).toFixed(2)}` },
        { label: "Rewards Distributed", value: `$${(metrics?.financials.total_rewards_distributed || 0).toFixed(2)}` },
      ],
      icon: "💎",
      color: "bg-purple-500/20 text-purple-400",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-gray-400 mt-2">Platform overview and metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <div key={stat.title} className="card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">{stat.title}</p>
                <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center text-2xl`}>
                {stat.icon}
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {stat.details.map((detail) => (
                <div key={detail.label} className="flex justify-between text-sm">
                  <span className="text-gray-500">{detail.label}</span>
                  <span className="text-white font-medium">{detail.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/admin/users"
            className="flex items-center gap-4 p-4 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors"
          >
            <span className="text-2xl">👥</span>
            <div>
              <p className="text-white font-medium">Manage Users</p>
              <p className="text-gray-400 text-sm">View and update user accounts</p>
            </div>
          </a>
          <a
            href="/admin/surveys"
            className="flex items-center gap-4 p-4 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors"
          >
            <span className="text-2xl">📋</span>
            <div>
              <p className="text-white font-medium">View Surveys</p>
              <p className="text-gray-400 text-sm">Monitor all platform surveys</p>
            </div>
          </a>
          <a
            href="/admin/transactions"
            className="flex items-center gap-4 p-4 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors"
          >
            <span className="text-2xl">💰</span>
            <div>
              <p className="text-white font-medium">Transactions</p>
              <p className="text-gray-400 text-sm">Review transaction history</p>
            </div>
          </a>
        </div>
      </div>

      {/* Platform Health */}
      <div className="card">
        <h2 className="text-xl font-bold text-white mb-4">Platform Health</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">API Status</span>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
              Operational
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Database</span>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
              Connected
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Environment</span>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
              Production
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
